import { useState, useCallback } from 'react';
import type { ManagedStreamingDetails, ManagedStreamingStatus } from '@/types/managedStreaming';
import {
  managedStreamingStatusLabels,
  managedStreamingStatusDescriptions,
  managedStreamingStatusColors,
  managedStreamingStatusIcons,
  isProvisioningTransient,
  isProvisioningActive,
  isProvisioningActionable,
} from '@/types/managedStreaming';
import { managedStreaming } from '@/services/managedStreaming';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface Props {
  /** Datos del streaming administrado (desde mock o Supabase) */
  data: ManagedStreamingDetails;
  /** ID de la radio para operaciones de retry/request */
  radioId?: string;
  /** Callback cuando los datos cambian (ej. después de un retry) */
  onRefresh?: () => void;
}

export default function ManagedStreamingSection({ data, radioId, onRefresh }: Props) {
  const [retrying, setRetrying] = useState(false);
  const mode = data.streamingMode;
  const status: ManagedStreamingStatus = data.provisioningStatus;
  const colors = managedStreamingStatusColors[status];
  const icon = managedStreamingStatusIcons[status];
  const label = managedStreamingStatusLabels[status];
  const description = managedStreamingStatusDescriptions[status];

  const handleRetry = useCallback(async () => {
    if (!radioId || retrying) return;

    setRetrying(true);
    try {
      await managedStreaming.retryProvisioning(radioId);
      SwalAlert.toast('Solicitud de aprovisionamiento reenviada. El equipo de RFW la procesará pronto.', 'success');
      onRefresh?.();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al reintentar el aprovisionamiento.';
      SwalAlert.toast(msg, 'error');
    } finally {
      setRetrying(false);
    }
  }, [radioId, retrying, onRefresh]);

  // ── EXTERNAL STREAM ──
  if (mode === 'EXTERNAL_STREAM') {
    return (
      <div className="space-y-4">
        <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-2">
          <i className="ri-plug-line text-foreground-500 text-xs"></i>
          Streaming Propio
        </h4>

        <div className={`p-5 rounded-2xl border ${colors.bg} ${colors.border}`}>
          <div className="flex items-start gap-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${colors.dot.replace('animate-pulse', '').replace('bg-', 'bg-').replace('-500', '-500')} bg-opacity-20`}>
              <i className={`${icon} ${colors.text} text-lg`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h5 className="text-foreground-950 font-semibold text-sm">STREAMING PROPIO</h5>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}></span>
                  {label}
                </span>
              </div>
              <p className="text-foreground-500 text-[11px] leading-relaxed">
                Tu radio transmite con su propio servidor de streaming externo. RadioFrecuenciaWeb consume tu señal y la distribuye dentro de la plataforma.
              </p>

              {/* Stream URL */}
              {data.externalStreamUrl && (
                <div className="mt-4 p-3 rounded-xl bg-background-100 border border-background-200">
                  <p className="text-foreground-400 text-[9px] font-semibold uppercase mb-1">Stream URL pública autorizada</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-white border border-background-200 rounded-lg px-3 py-1.5 text-foreground-800 text-[11px] font-mono break-all">
                      {data.externalStreamUrl}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(data.externalStreamUrl || '');
                        SwalAlert.toast('Stream URL copiada', 'success');
                      }}
                      className="w-8 h-8 rounded-lg bg-foreground-950 text-background-50 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors shrink-0"
                    >
                      <i className="ri-file-copy-line text-xs"></i>
                    </button>
                  </div>
                  {data.externalStreamValidated && (
                    <div className="flex items-center gap-1.5 mt-2">
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-[11px]"></i>
                      <span className="text-emerald-600 text-[10px] font-medium">URL validada</span>
                    </div>
                  )}
                </div>
              )}

              {/* Solicitar nueva validación */}
              <p className="text-foreground-400 text-[10px] mt-3">
                ¿Cambiaste de servidor? Solicitá una nueva validación desde la configuración de streaming.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── RFW MANAGED ──
  const transient = isProvisioningTransient(status);
  const active = isProvisioningActive(status);
  const actionable = isProvisioningActionable(status);

  return (
    <div className="space-y-4">
      <h4 className="text-foreground-700 text-[10px] font-semibold uppercase tracking-wider flex items-center gap-2">
        <i className="ri-cloud-line text-foreground-500 text-xs"></i>
        Streaming Administrado por RFW
      </h4>

      {/* Status Banner */}
      <div className={`p-5 rounded-2xl border ${colors.bg} ${colors.border}`}>
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-white`}>
            <i className={`${icon} ${colors.text} text-lg ${status === 'PROVISIONING' ? 'animate-spin' : ''}`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h5 className="text-foreground-950 font-semibold text-sm">STREAMING ADMINISTRADO POR RFW</h5>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}></span>
                {label}
              </span>
            </div>
            <p className="text-foreground-500 text-[11px] leading-relaxed">{description}</p>

            {/* Retry button */}
            {actionable && radioId && (
              <button
                onClick={handleRetry}
                disabled={retrying}
                className={`mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[11px] font-bold transition-colors cursor-pointer whitespace-nowrap ${
                  retrying
                    ? 'bg-background-200 text-foreground-300 cursor-not-allowed'
                    : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 active:scale-[0.98]'
                }`}
              >
                {retrying ? (
                  <i className="ri-loader-4-line animate-spin text-xs"></i>
                ) : (
                  <i className="ri-refresh-line text-xs"></i>
                )}
                {retrying ? 'Reintentando...' : 'Reintentar aprovisionamiento'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error details (sanitized) */}
      {status === 'FAILED' && data.lastError && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 text-sm mt-px shrink-0"></i>
          <div>
            <p className="text-red-700 text-[11px] font-semibold mb-0.5">Detalle del error</p>
            <p className="text-red-600 text-[10px] leading-relaxed">{data.lastError}</p>
          </div>
        </div>
      )}

      {/* Plan + Features */}
      {data.planName && (
        <div className="p-5 rounded-2xl bg-background-50 border border-background-200 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider">Plan contratado</p>
              <p className="text-foreground-950 font-bold text-sm mt-0.5">{data.planName}</p>
            </div>
            {data.planPrice && (
              <span className="px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[11px] font-bold whitespace-nowrap">
                {data.planPrice}
              </span>
            )}
          </div>

          {/* Features grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* AutoDJ */}
            <FeatureBadge
              included={data.autodjIncluded}
              icon="ri-disc-line"
              label="AutoDJ"
              description="Programación automática 24/7"
            />
            {/* Streaming Hosting */}
            <FeatureBadge
              included={data.streamingHostingIncluded}
              icon="ri-cloud-line"
              label="Hosting RFW"
              description="Servidores administrados"
            />
            {/* Storage */}
            <FeatureBadge
              included={data.storageGb !== null && data.storageGb > 0}
              icon="ri-hard-drive-2-line"
              label="Almacenamiento"
              description={data.storageGb ? `${data.storageGb} GB` : 'No incluido'}
            />
            {/* Bitrate */}
            <FeatureBadge
              included={data.bitrate !== null}
              icon="ri-sound-module-line"
              label="Bitrate"
              description={data.bitrate ? `${data.bitrate} kbps` : 'Pendiente'}
            />
            {/* Listeners */}
            <FeatureBadge
              included={data.listenersMax !== null}
              icon="ri-headphone-line"
              label="Oyentes máx."
              description={data.listenersMax ? data.listenersMax.toLocaleString() : 'Pendiente'}
            />
            {/* Plan features */}
            {data.planFeatures.slice(0, 1).map((f, i) => (
              <FeatureBadge
                key={i}
                included={true}
                icon="ri-check-line"
                label="Funciones"
                description={f}
              />
            ))}
          </div>

          {/* Extra features list */}
          {data.planFeatures.length > 1 && (
            <div className="pt-3 border-t border-background-200">
              <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-2">Funciones incluidas</p>
              <ul className="space-y-1">
                {data.planFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-foreground-600 text-[10px]">
                    <i className="ri-check-line text-emerald-500 text-[10px] mt-px shrink-0"></i>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Stream URL — solo cuando está confirmada */}
      {status === 'ACTIVE' && data.streamUrl && (
        <div className="p-4 rounded-2xl bg-background-50 border border-background-200">
          <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-2">Stream URL pública</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-background-100 border border-background-200 rounded-xl px-3 py-2 text-foreground-800 text-xs font-mono break-all">
              {data.streamUrl}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(data.streamUrl || '');
                SwalAlert.toast('Stream URL copiada', 'success');
              }}
              className="w-9 h-9 rounded-xl bg-foreground-950 text-background-50 flex items-center justify-center cursor-pointer hover:bg-foreground-800 transition-colors shrink-0"
            >
              <i className="ri-file-copy-line text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="p-4 rounded-2xl bg-background-50 border border-background-200 space-y-2">
        <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider">Línea de tiempo</p>
        <div className="space-y-2">
          {data.requestedAt && (
            <TimelineItem
              label="Solicitud"
              value={new Date(data.requestedAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              done={true}
            />
          )}
          {data.lastAttemptAt && (
            <TimelineItem
              label="Último intento"
              value={new Date(data.lastAttemptAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              done={active}
              isLast
            />
          )}
          {transient && (
            <TimelineItem
              label="Stream URL"
              value="Pendiente de aprovisionamiento"
              done={false}
              isLast
            />
          )}
          {active && data.streamUrl && (
            <TimelineItem
              label="Stream URL"
              value="Confirmada por el backend"
              done={true}
              isLast
            />
          )}
        </div>
      </div>

      {/* Nota: no se muestran credenciales */}
      <p className="text-foreground-300 text-[9px] italic">
        Las credenciales del servidor son gestionadas exclusivamente por el backend de RadioFrecuenciaWeb. No se muestran aquí por seguridad.
      </p>
    </div>
  );
}

// ── Sub-components ──

function FeatureBadge({ included, icon, label, description }: {
  included: boolean;
  icon: string;
  label: string;
  description: string;
}) {
  return (
    <div className={`flex items-center gap-2.5 p-3 rounded-xl ${included ? 'bg-emerald-50 border border-emerald-200' : 'bg-background-100 border border-background-200 opacity-60'}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${included ? 'bg-white' : 'bg-background-200'}`}>
        <i className={`${icon} ${included ? 'text-emerald-600' : 'text-foreground-400'} text-sm`}></i>
      </div>
      <div className="min-w-0">
        <p className={`text-[11px] font-semibold ${included ? 'text-foreground-800' : 'text-foreground-400'}`}>{label}</p>
        <p className={`text-[10px] ${included ? 'text-emerald-600' : 'text-foreground-400'}`}>{description}</p>
      </div>
    </div>
  );
}

function TimelineItem({ label, value, done, isLast }: {
  label: string;
  value: string;
  done: boolean;
  isLast?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center shrink-0 pt-0.5">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${done ? 'bg-emerald-500' : 'bg-background-300'}`}>
          {done ? (
            <i className="ri-check-line text-white text-[9px]"></i>
          ) : (
            <i className="ri-more-line text-white text-[9px]"></i>
          )}
        </div>
        {!isLast && <div className={`w-px h-full min-h-[16px] mt-0.5 ${done ? 'bg-emerald-300' : 'bg-background-200'}`}></div>}
      </div>
      <div className="pb-3">
        <p className="text-foreground-700 text-[10px] font-semibold">{label}</p>
        <p className="text-foreground-400 text-[10px]">{value}</p>
      </div>
    </div>
  );
}